import React from "react";
import PropTypes from "prop-types";

/**
 * Gradient placeholder tile built from a business name's initials.
 *
 * Replaces the hotlinked Unsplash photo the portfolio used as its fallback:
 * no third-party request, no licensing exposure, nothing to 404, and each
 * business gets a stable, recognisable tile instead of everyone sharing one
 * generic stock image.
 */

// Fixed brand-family gradients. Picked by hashing the name so a given business
// always renders the same tile across reloads and machines.
const GRADIENTS = [
  "from-brand-500 to-brand-700",
  "from-brand-400 to-brand-600",
  "from-brand-600 to-brand-800",
  "from-emerald-500 to-brand-700",
  "from-brand-500 to-emerald-700",
  "from-lime-500 to-brand-700",
];

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";

const hashOf = (str) => {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const Monogram = ({ name, className = "" }) => {
  const gradient = GRADIENTS[hashOf(name) % GRADIENTS.length];

  return (
    <div
      className={`flex items-center justify-center bg-gradient-to-br ${gradient} ${className}`}
      role="img"
      aria-label={name}
    >
      <span className="text-4xl font-bold tracking-tight text-white/90 drop-shadow-sm">
        {initialsOf(name)}
      </span>
    </div>
  );
};

Monogram.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
};

export default Monogram;
